import { effect, Injectable, signal, inject } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Bank, CustomerExtended } from '../interfaces'
import { Observable, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { LoginTypes } from '../types'
import * as Sentry from '@sentry/angular'

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient)

  readonly storageKey = 'user'

  customer = signal<CustomerExtended | null>(null)
  error = signal('')

  constructor() {
    const user = sessionStorage.getItem(this.storageKey)
    this.customer.set(user ? JSON.parse(user) : null)

    effect(() => {
      const customer = this.customer()
      if (customer) {
        Sentry.setUser({ user_id: customer.id })
      } else {
        Sentry.setUser(null)
      }
    })
  }

  get user(): CustomerExtended | null {
    return this.customer()
  }

  get isNewCustomer() {
    return Boolean(+this.customer()?.credit_card)
  }

  setUser(response: any) {
    if (response && response.user) {
      this.customer.set(response.user)
      sessionStorage.setItem(this.storageKey, JSON.stringify(response.user))
    } else {
      this.customer.set(null)
      sessionStorage.removeItem(this.storageKey)
    }
  }

  handleError(errorResponse: HttpErrorResponse) {
    const error = errorResponse.error.error.alias ?? errorResponse.message ?? ''
    this.error.set(error)
    return throwError(() => new Error(error))
  }

  login(credentials: any, loginType: LoginTypes): Observable<any> {
    return this.http
      .post(`/api/v1/users/login/${loginType}`, { credentials })
      .pipe(tap(this.setUser.bind(this)), catchError(this.handleError.bind(this)))
  }

  authorizeBank(data: Bank): Observable<any> {
    return this.http
      .post(`/api/public/v1/users/authorize/bank`, { data })
      .pipe(tap(this.setUser.bind(this)), catchError(this.handleError.bind(this)))
  }

  isSetupRequired(): boolean {
    return Boolean(+this.user?.initial_3D_secure_required)
  }

  logout() {
    this.setUser({ user: null })
  }
}
